import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { personTypesApi, personsApi } from '../../services/apiClient';
import { usePermissions } from '../../contexts/PermissionContext';
import { useIsMobile } from '../../hooks/useResponsive';
import { useThemeSettings } from '../../contexts/ThemeContext';
import PersonTypeDialog from './components/PersonTypeDialog';

const PersonTypeManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();
  const { t } = useThemeSettings();

  // Data state
  const [personTypes, setPersonTypes] = useState([]);
  const [personCounts, setPersonCounts] = useState({});

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Permission check
  const canManage = hasPermission('admin') || hasPermission('persontypes.manage');

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [typesResponse, personsResponse] = await Promise.all([
        personTypesApi.getAll({ limit: 1000 }),
        personsApi.getAll({ limit: 5000 })
      ]);

      if (typesResponse.data.success || typesResponse.data.data) {
        setPersonTypes(typesResponse.data.data || []);
      }

      // Calculate person counts per type
      if (personsResponse.data.success || personsResponse.data.data) {
        const persons = personsResponse.data.data || [];
        const counts = {};
        persons.forEach(person => {
          const typeId = person.personType;
          counts[typeId] = (counts[typeId] || 0) + 1;
        });
        setPersonCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching person types:', error);
      enqueueSnackbar('Failed to load person types', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle dialog
  const handleOpenDialog = (type = null) => {
    setEditingType(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingType(null);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingType) {
        await personTypesApi.update(editingType.id, formData);
        enqueueSnackbar('Person type updated successfully', { variant: 'success' });
      } else {
        await personTypesApi.create(formData);
        enqueueSnackbar('Person type created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving person type:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to save person type',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleOpenDeleteDialog = (type) => {
    setDeleteTarget(type);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setSaving(true);
    try {
      await personTypesApi.delete(deleteTarget.id);
      enqueueSnackbar('Person type deleted successfully', { variant: 'success' });
      handleCloseDeleteDialog();
      fetchData();
    } catch (error) {
      console.error('Error deleting person type:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to delete person type',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  // Get field summary for display
  const getFieldSummary = (dataModel) => {
    if (!dataModel || !Array.isArray(dataModel) || dataModel.length === 0) {
      return 'No custom fields';
    }
    const fieldLabels = dataModel.map(f => f.label || f.key);
    if (fieldLabels.length <= 3) {
      return fieldLabels.join(', ');
    }
    return `${fieldLabels.slice(0, 3).join(', ')} +${fieldLabels.length - 3} more`;
  };

  // Render mobile card view
  const renderMobileCard = (type) => {
    const personCount = personCounts[type.id] || 0;
    const hasPersons = personCount > 0;

    return (
      <Card key={type.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="div">
              {type.friendlyName}
            </Typography>
            <Chip
              icon={<PersonIcon sx={{ fontSize: 16 }} />}
              label={personCount}
              size="small"
              color={hasPersons ? 'primary' : 'default'}
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {getFieldSummary(type.dataModel)}
          </Typography>
          {type.dataModel && type.dataModel.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {type.dataModel.slice(0, 5).map((field, idx) => (
                <Chip
                  key={idx}
                  label={field.label || field.key}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {type.dataModel.length > 5 && (
                <Chip
                  label={`+${type.dataModel.length - 5}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}
        </CardContent>
        {canManage && (
          <CardActions>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleOpenDialog(type)}
            >
              Edit
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleOpenDeleteDialog(type)}
              disabled={hasPersons}
            >
              Delete
            </Button>
          </CardActions>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CategoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              Person Types
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage person types and their custom field schemas
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Person Type
            </Button>
          )}
        </Box>
      </Box>

      {/* Content */}
      {personTypes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CategoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Person Types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create person types to define different categories of persons with custom fields.
          </Typography>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Person Type
            </Button>
          )}
        </Paper>
      ) : isMobile ? (
        // Mobile card view
        <Stack spacing={0}>
          {personTypes.map(renderMobileCard)}
        </Stack>
      ) : (
        // Desktop table view
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Custom Fields</TableCell>
                <TableCell align="center">Persons</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {personTypes.map((type) => {
                const personCount = personCounts[type.id] || 0;
                const hasPersons = personCount > 0;

                return (
                  <TableRow key={type.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">
                        {type.friendlyName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {type.dataModel && type.dataModel.length > 0 ? (
                          <>
                            {type.dataModel.slice(0, 4).map((field, idx) => (
                              <Chip
                                key={idx}
                                label={field.label || field.key}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                            {type.dataModel.length > 4 && (
                              <Chip
                                label={`+${type.dataModel.length - 4}`}
                                size="small"
                                color="default"
                              />
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No custom fields
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<PersonIcon sx={{ fontSize: 16 }} />}
                        label={personCount}
                        size="small"
                        color={hasPersons ? 'primary' : 'default'}
                        variant={hasPersons ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(type)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={hasPersons ? 'Cannot delete: has persons' : 'Delete'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(type)}
                              disabled={hasPersons}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Person Type Dialog */}
      <PersonTypeDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
        personType={editingType}
        saving={saving}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Person Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the person type "{deleteTarget?.friendlyName}"?
            This action cannot be undone.
          </DialogContentText>
          {deleteTarget && personCounts[deleteTarget.id] > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              This person type has {personCounts[deleteTarget.id]} person(s) assigned.
              Please reassign or delete those persons first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={saving || (deleteTarget && personCounts[deleteTarget.id] > 0)}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PersonTypeManagement;
